/**
 * Event Form Component
 * Form for creating and editing events
 *
 * TODO Phase 1 Enhancements:
 * - Date range picker (single date or date range with start/end times)
 * - External links section (add/edit/remove with labels)
 * - Full field support for all Event properties
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useCreateEvent, useUpdateEvent } from "../../hooks/useEvents";
import { afterEventLinksSaved } from "../../services/eventCTAAutoPrepare.service";
import {
  getAvailabilityForPositions,
  leadRepSyncNotice,
  syncLeadRepToSalesReps,
} from "../../services/salesCalendar.service";
import { describeDateModelError } from "../../services/eventDateModel.service";
import { AVAILABILITY_TYPE_LABELS } from "../../types/salesCalendar.types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../services/supabase";
import {
  getEventOrganizations,
  getEventLocations,
  saveEventOrgLocations,
  getEventLinks,
  saveEventLinks,
  getEventListAssociations,
  saveEventListAssociations,
} from "../../services/events.service";
import Modal from "../ui/Modal";
import {
  US_STATES,
  CLASS_DELIVERY_METHODS,
  USLS_CURRICULUM_OPTIONS,
  CLASS_SUBTYPES,
  EVENT_OWNERSHIP_OPTIONS,
  VOUCHER_USED_EVENT_TYPES,
  SUBSCRIPTION_ELIGIBLE_EVENT_TYPES,
  EVENT_TYPE_CLASSIFICATION,
} from "../../types/event.types";
import { EVENT_TYPES, VISIBLE_EVENT_TYPES } from "../../utils/constants";
import { getDateFromISO, getTimeFromISO } from "../../utils/eventTime";
import {
  eibAudienceLabel,
  isEibAudienceLink,
} from "../../utils/eibAudienceLink";
import type { Event } from "../../types/event.types";
import { useAuth } from "../../contexts/AuthContext";
import { completedOptionState } from "../../utils/eventCompletePermission";
import Button from "../common/Button";
import Input from "../common/Input";
import { useToast } from "../ui/Toast";
import EventOrganizationLocationSelector, {
  type EventOrgLocationSelection,
} from "./EventOrganizationLocationSelector";
import { resolveEndDateTime } from "./eventEndDateTime";
import EventDailyHoursEditor, {
  EMPTY_DAILY_HOURS,
  UNLOADED_DAILY_HOURS,
  initialDailyHoursValue,
  planDayHoursSave,
  type DailyHoursValue,
} from "./EventDailyHoursEditor";
import { dailyHoursApplicabilityLocal } from "../../utils/eventDayHours";
import {
  canEditEventDayHours,
  describeDayHoursError,
  getEventDayHoursForEvent,
  setEventDayHours,
} from "../../services/eventDayHours.service";
import DateRangePicker, {
  type DateRange,
  validateDateRange,
} from "./DateRangePicker";
import EventExternalLinksManager, {
  type EventLinkInput,
} from "./EventExternalLinksManager";
import EventSegmentSelector, {
  type EventSegmentSelection,
} from "./EventSegmentSelector";
import PromoterSelector from "./PromoterSelector";
import {
  eventVenueError,
  venueLinkRequired,
} from "../../utils/eventVenueRequirement";
import { XMarkIcon } from "@heroicons/react/24/outline";
import TimezoneSelector, {
  DEFAULT_TIMEZONE,
  getTimezoneOffset,
} from "../common/TimezoneSelector";
import SalesHierarchyChainNote from "./SalesHierarchyChainNote";
import {
  fillFromLeadChain,
  type ManagerLevels,
} from "../../utils/repReportingChain";
import {
  autoEventTimezone,
  describeTimezoneSource,
  timezoneUnsetNote,
  type AutoEventTimezone,
} from "../../utils/zipTimezone";
import {
  sortSalesPositionsByFirstName,
  displaySalesPositionName,
  LEAD_EVENT_REP_POSITION_TYPES,
  TERRITORY_MANAGER_POSITION_TYPES,
  REGIONAL_MANAGER_POSITION_TYPES,
  SALES_DIRECTOR_POSITION_TYPES,
} from "../../utils/salesPositionDisplay";
import {
  eventStatusChangeBlockReason,
  eventTypeIsNoPromo,
} from "../../utils/eventTypePolicy";
import {
  hasLocationAddress,
  clearLocationAddress,
} from "../../utils/eventLocationFields";
import {
  SELF_SERVICE_REP_VALUE,
  SELF_SERVICE_REP_LABEL,
  repSelectValue,
  parseRepSelectChange,
  resolveLeadRepPositionId,
  saveEditedLeadRepAndEvent,
} from "../../utils/eventSelfService";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";
import ConfirmDialog from "../ui/ConfirmDialog";
import { useTimeOff } from "../../hooks/useTimeOff";
import {
  datesInclusive,
  timeOffSentence,
} from "../../services/timeOffConflicts";
import { getEventSalesAssignments } from "../../services/salesCalendar.service";

interface EventFormProps {
  event?: Event;
  /** Called after a successful save. On CREATE of a capability-rich event, `guidedMode`
   *  is the EIB workflow to open — 'pre_register' when registration is required, else
   *  'show_up' — and the caller routes into the Event-in-a-Box wizard. null = no hand-off. */
  onSuccess?: (
    eventId?: string,
    guidedMode?: "show_up" | "pre_register" | null,
  ) => void;
  onCancel?: () => void;
}

interface Profile {
  id: string;
  full_name: string;
  email: string;
}

// Types for event association responses
interface OrgAssociation {
  organizations?: { id: string; name: string };
}

interface LocAssociation {
  locations?: {
    id: string;
    name: string;
    organization_id: string;
    organizations?: { name: string };
  };
}

interface LinkAssociation {
  id: string;
  url: string;
  label: string;
  description?: string;
  link_type?: string;
  display_order?: number;
}

interface ListAssociation {
  list_id: string;
  association_type: string;
  eib_launch_id?: string | null;
  lists?: {
    name: string;
    list_type?: string | null;
    contact_count?: number | null;
  };
}

// Order-insensitive keys of the association selections (edit save skips unchanged writes).
const orgSelectionKey = (
  sel: Array<{ org_id: string; location_id?: string | null }>,
): string =>
  sel
    .map((x) => `${x.org_id}|${x.location_id ?? ""}`)
    .sort()
    .join(",");
const linksKey = (
  links: Array<{
    url?: string | null;
    label?: string | null;
    description?: string | null;
    link_type?: string | null;
    display_order?: number | null;
  }>,
): string =>
  JSON.stringify(
    links.map((l) => [
      l.url ?? "",
      l.label ?? "",
      l.description ?? "",
      l.link_type ?? "",
      l.display_order ?? null,
    ]),
  );
const segmentsKey = (
  sel: Array<{ segment_id: string; association_type?: string | null }>,
): string =>
  sel
    .map((x) => `${x.segment_id}|${x.association_type ?? ""}`)
    .sort()
    .join(",");

const EventForm: React.FC<EventFormProps> = ({
  event,
  onSuccess,
  onCancel,
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isEditing = !!event;
  const queryClient = useQueryClient();
  // 2026-06-07 post-deploy: lock the whole form when editing a
  // cancelled event (Heather R1 callout). Status was already
  // gated; this disables every other input + the submit button.
  // The Cancel button (back to Event Detail) stays enabled.
  const isCancelled = isEditing && event?.status === "cancelled";

  const createMutation = useCreateEvent();
  const updateMutation = useUpdateEvent();

  // Fetch users for Event Owner autocomplete
  const [users, setUsers] = useState<Profile[]>([]);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState("");
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .not("email", "like", "deleted_%")
        .neq("role", "readonly") // a read-only viewer never owns an event (owner 2026-11-03, mig 1623)
        .order("full_name");

      if (!error && data) {
        setUsers(data as Profile[]);
      }
    };

    fetchUsers();
  }, []);

  // Filter users based on search term, prioritizing current user
  const filteredUsers = users
    .filter((u) => {
      const searchLower = ownerSearchTerm.toLowerCase();
      return (
        u.full_name?.toLowerCase().includes(searchLower) ||
        u.email?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      // Current user always comes first
      if (a.id === user?.id) {
        return -1;
      }
      if (b.id === user?.id) {
        return 1;
      }
      // Then sort alphabetically by name
      return (a.full_name || a.email).localeCompare(b.full_name || b.email);
    });

  // Fetch active sales positions for Sales Rep dropdown
  const { data: salesPositions } = useQuery({
    // Distinct key per data shape (2026-10-16): the same key held different rows/columns on other
    // screens, and with the 5-minute staleTime whichever screen fetched first won.
    queryKey: ["sales-positions-active", "event-form-select"],
    queryFn: async () => {
      const { data } = await supabase
        .from("sales_positions")
        .select("id, title, assigned_name, position_type, parent_position_id")
        .eq("is_active", true)
        .order("assigned_name", { ascending: true, nullsFirst: false });
      return data || [];
    },
  });

  // Sales hierarchy chain state. Smart Admin replacement R2A (2026-05-17):
  // 4 position-id slots, each independently editable. Auto-populated from
  // the account's primary territorial assignment via derive_account_hierarchy
  // RPC; operator can override any field for matrixed cases (e.g. an event
  // covered by a manager from a different region per Heather's spec).
  //
  // Bug from pre-R2A: the old fetchHierarchy(positionId) climbed the REP's
  // parent_position_id chain, not the ACCOUNT's chain. Also searched for
  // position_type='sales_director' which never matched (real value is
  // 'director'). Both bugs fixed by switching to the RPC.
  const [hierarchyChain, setHierarchyChain] = useState<{
    repId: string | null;
    tmId: string | null;
    rmId: string | null;
    sdId: string | null;
  }>({ repId: null, tmId: null, rmId: null, sdId: null });

  // #14 (2026-06): "No Rep Required / Self-Service Event" — no lead/event rep;
  // signed partner runs it with the generic QR. Persisted on events.is_self_service.
  const [isSelfService, setIsSelfService] = useState<boolean>(
    event?.is_self_service ?? false,
  );

  // Tracks whether the operator has manually edited any hierarchy field
  // since the last auto-derive. Used to gate the "overwrite on org change"
  // confirmation modal (audit verdict: re-derive on org change but never
  // silently overwrite manual values).
  const hierarchyManuallyOverridden = useRef(false);
  // GS-06: the account chain from the last derive_account_hierarchy call — only the
  // fallback when the Lead/Event Rep has no reporting chain of their own.
  const accountLevelsRef = useRef<ManagerLevels | null>(null);
  // GS-06: true once the operator picked a TM/RM/SD by hand (a rep change then never
  // replaces those with the account chain).
  const managersHandEditedRef = useRef(false);
  // The Lead/Event Rep as loaded (or as last changed by this form): a save sends a rep change only when the rep
  // control differs from it, compared-and-set against it (MSR activation fix 6, internal re-audit P2-3).
  const loadedRepRef = useRef<{ repId: string | null; isSelfService: boolean }>(
    {
      repId: event?.sales_position_id ?? null,
      isSelfService: event?.is_self_service ?? false,
    },
  );

  const [pendingOrgChange, setPendingOrgChange] = useState<{
    nextOrgId: string;
    nextHierarchy: {
      repId: string | null;
      tmId: string | null;
      rmId: string | null;
      sdId: string | null;
    };
  } | null>(null);

  type HierarchyDerivation = {
    assignment_found: boolean;
    multi_primary_warning: boolean;
    account_owner_position_id: string | null;
    account_owner_position_type: string | null;
    sales_rep_position_id: string | null;
    territory_manager_position_id: string | null;
    regional_manager_position_id: string | null;
    director_position_id: string | null;
    vp_position_id: string | null;
  };

  const deriveHierarchyForOrg = useCallback(
    async (
      organizationId: string | null,
    ): Promise<HierarchyDerivation | null> => {
      if (!organizationId) {
        return null;
      }
      const { data, error } = await supabase.rpc("derive_account_hierarchy", {
        p_organization_id: organizationId,
      });
      if (error || !data || data.length === 0) {
        return null;
      }
      return data[0] as HierarchyDerivation;
    },
    [],
  );

  // Form state - default owner to current user for new events
  const [formData, setFormData] = useState({
    title: event?.title || "",
    description: event?.description || "",
    notes: event?.notes || "",
    type: event?.type || "",
    status: event?.status || "scheduled",
    organizer_id: event?.organizer_id || user?.id || "",
    state: event?.state || "", // Regulatory state (required for CTAs)
    location_type: event?.location_type || "physical",
    location_name: event?.location_name || "",
    location_address_line1: event?.location_address?.address_line1 || "",
    location_city: event?.location_address?.city || "",
    location_state: event?.location_address?.state || "",
    location_postal_code: event?.location_address?.postal_code || "",
    meeting_url: event?.meeting_url || "",
    is_public: event?.is_public || false,
    is_featured: event?.is_featured || false,
    registration_required: event?.registration_required || false,
    max_attendees: event?.max_attendees || "",
    pre_registration_cap: event?.pre_registration_cap ?? "",
    // Walk-ins default ON for new events (classification-redesign #3); edits preserve.
    allow_walk_ins: event ? (event.allow_walk_ins ?? false) : true,
    walk_in_limit: event?.walk_in_limit || "",
    // Promoter organization ID and name (for conferences and other promoted events)
    promoter_id: (event as any)?.promoter_id || "",
    promoter_name: (event as any)?.promoter_name || "",
    // Attendance list auto-creation
    create_attendance_list: event?.create_attendance_list || false,
    // Class sub-classifications
    class_delivery_method: event?.class_delivery_method || "",
    usls_curriculum: event?.usls_curriculum || "",
    class_subtype: event?.class_subtype || "",
    // Event ownership & sales position
    event_ownership: event?.event_ownership || "",
    sales_position_id: event?.sales_position_id || "",
    // Voucher used & USLS curriculum used
    voucher_used: event?.voucher_used || false,
    usls_curriculum_used: event?.usls_curriculum_used || false,
  });

  // Classification toggle (UI-only): "Is this a conversion engine?". Paired with
  // "Registration required?" (= formData.registration_required), it drives which types
  // the Event Type dropdown offers. Conversion-ness is NOT stored — it derives from
  // `type` (approach C, 2026-07-28). Initialized from the selected type's classification.
  const [isConversionEngine, setIsConversionEngine] = useState<boolean>(() =>
    event
      ? (EVENT_TYPE_CLASSIFICATION[event.type || ""]?.conversion ?? false)
      : // New events default to a recruitment event (gun show is the primary,
        // highest-volume use), so the main path is front-and-center.
        true,
  );

  // Changing a classification toggle re-filters the Event Type options; if the current
  // selection no longer fits the new (conversion, registration) combo, clear it so the
  // operator re-picks from the matching set.
  const handleClassificationChange = (
    conversion: boolean,
    registration: boolean,
  ) => {
    setIsConversionEngine(conversion);
    setFormData((prev) => {
      const cls = EVENT_TYPE_CLASSIFICATION[prev.type];
      const stillMatches =
        !!cls &&
        cls.conversion === conversion &&
        cls.registration === registration;
      return {
        ...prev,
        registration_required: registration,
        type: stillMatches ? prev.type : "",
      };
    });
  };

  // Event Type options filtered by the current classification toggles. The
  // currently-selected type is always retained (edit / deprecated values) even if it
  // doesn't match, so a stored type is never hidden.
  const classifiedTypeOptions = (() => {
    const matches = VISIBLE_EVENT_TYPES.filter((t) => {
      const cls = EVENT_TYPE_CLASSIFICATION[t.value];
      return (
        !!cls &&
        cls.conversion === isConversionEngine &&
        cls.registration === formData.registration_required
      );
    });
    if (!formData.type || matches.some((t) => t.value === formData.type)) {
      return matches;
    }
    return [
      ...matches,
      ...EVENT_TYPES.filter((t) => t.value === formData.type),
    ];
  })();

  // R2A: hydrate hierarchy from existing event on edit. For NEW events,
  // the hierarchy populates when the operator picks an organization via
  // the EventOrganizationLocationSelector (handled in handleOrgSelected
  // below). The state was previously driven by formData.sales_position_id
  // which created the rep-chain bug.
  useEffect(() => {
    if (event) {
      setHierarchyChain({
        repId: event.sales_position_id ?? null,
        tmId: event.territory_manager_position_id ?? null,
        rmId: event.regional_manager_position_id ?? null,
        sdId: event.sales_director_position_id ?? null,
      });
      setIsSelfService(event.is_self_service ?? false);
      loadedRepRef.current = {
        repId: event.sales_position_id ?? null,
        isSelfService: event.is_self_service ?? false,
      };
    }
  }, [event]);

  // Separate state for date range - parse dates in event timezone to prevent date shifting
  const eventTz = event?.timezone || DEFAULT_TIMEZONE;
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: event?.date_tbd
      ? ""
      : getDateFromISO(event?.start_time, eventTz),
    startTime: event?.date_tbd
      ? ""
      : getTimeFromISO(event?.start_time, eventTz),
    endDate: event?.date_tbd ? "" : getDateFromISO(event?.end_time, eventTz),
    endTime: event?.date_tbd ? "" : getTimeFromISO(event?.end_time, eventTz),
    allDay: event?.all_day || false,
    isMultiDay:
      getDateFromISO(event?.start_time, eventTz) !==
      getDateFromISO(event?.end_time, eventTz),
  });

  // The start date as LOADED (edit only). Editing an event that already happened must not be blocked by the
  // "start date cannot be in the past" rule when the operator did not touch the date (that rule is for choosing a
  // date); it used to stop every Edit-form save of a past event, for every role.
  const loadedStartDateRef = useRef<string>(dateRange.startDate);

  // EVT-08b (owner 2026-10-30): the time zone comes from the venue ZIP ONLY — never from the state
  // or DEFAULT_TIMEZONE. Create: unset until a recognised ZIP is entered or the operator chooses.
  // Edit: the saved zone is kept unless the ZIP is changed to one that resolves (a saved zone is
  // never cleared). Once the operator picks a zone by hand the ZIP stops overriding it.
  const savedTzBaseline = useRef(
    event
      ? {
          timezone: event.timezone ?? null,
          // formData was seeded from the event above (same normalisation as the inputs).
          locationType: formData.location_type,
          postalCode: formData.location_postal_code,
        }
      : null,
  );
  const [tzAuto, setTzAuto] = useState<AutoEventTimezone>(() =>
    autoEventTimezone({
      locationType: formData.location_type,
      postalCode: formData.location_postal_code,
      saved: savedTzBaseline.current,
    }),
  );
  const [timezone, setTimezone] = useState<string>(tzAuto.timezone);
  const timezoneManuallySet = useRef(false);
  // Bumped when the operator picks the blank "Select timezone..." option, so the ZIP rule resumes.
  const [tzRederive, setTzRederive] = useState(0);
  useEffect(() => {
    if (timezoneManuallySet.current) {
      return;
    }
    const auto = autoEventTimezone({
      locationType: formData.location_type,
      postalCode: formData.location_postal_code,
      saved: savedTzBaseline.current,
    });
    setTimezone(auto.timezone);
    setTzAuto(auto);
  }, [formData.location_postal_code, formData.location_type, tzRederive]);

  // Date TBD state - when true, dates are not yet confirmed
  const [dateTbd, setDateTbd] = useState(event?.date_tbd || false);

  // Hours each day (migration 2026-10-27-1120): optional per-date opening hours for a multi-day event. Loaded on
  // edit, saved AFTER the event (set_event_day_hours replaces the whole set), never part of the event update.
  const dailyHoursApp = useMemo(
    () =>
      dailyHoursApplicabilityLocal({
        startDate: dateRange.startDate,
        startTime: dateRange.startTime,
        endDate: dateRange.endDate,
        endTime: dateRange.endTime,
        allDay: dateRange.allDay,
        dateTbd,
        timezone,
      }),
    [dateRange, dateTbd, timezone],
  );
  // Every row saved for the event. `savedDayHours` is undefined until the read SUCCEEDS ([] = none saved); a failed
  // read is shown and leaves the hours untouched (external audit 705a4bd9 F1: "not loaded" must never look like
  // "turned off").
  const savedDayHoursQuery = useQuery({
    queryKey: ["event-day-hours", "form", event?.id],
    queryFn: () => getEventDayHoursForEvent(event!.id),
    enabled: !!event?.id,
    staleTime: 0,
  });
  const savedDayHours = isEditing
    ? savedDayHoursQuery.isSuccess
      ? savedDayHoursQuery.data
      : undefined
    : [];
  const { data: canEditDayHours, isError: canEditDayHoursFailed } = useQuery({
    queryKey: ["event-day-hours", "can-edit", event?.id],
    queryFn: () => canEditEventDayHours(event!.id),
    enabled: !!event?.id,
    staleTime: 0,
  });
  // Create: initialized empty. Edit: UNLOADED until the first successful read, then initialized exactly once; a later
  // refetch never replaces the operator's draft, and an uninitialized draft never writes.
  const [dailyHours, setDailyHours] = useState<DailyHoursValue>(
    isEditing ? UNLOADED_DAILY_HOURS : EMPTY_DAILY_HOURS,
  );
  useEffect(() => {
    if (
      isEditing &&
      !dailyHours.initialized &&
      savedDayHoursQuery.isSuccess &&
      !savedDayHoursQuery.isFetching
    ) {
      setDailyHours(initialDailyHoursValue(savedDayHoursQuery.data));
    }
  }, [
    isEditing,
    dailyHours.initialized,
    savedDayHoursQuery.isSuccess,
    savedDayHoursQuery.isFetching,
    savedDayHoursQuery.data,
  ]);
  const dailyHoursLoadError = savedDayHoursQuery.isError
    ? describeDayHoursError(savedDayHoursQuery.error)
    : null;
  // Edit: only the event's creator, an editor or an admin may change daily hours (the server refuses others).
  const dailyHoursReadOnlyReason =
    isEditing && canEditDayHours === false
      ? "Only the event's owner, an editor of the event, or an admin can change the hours for each day."
      : isEditing && canEditDayHoursFailed
        ? "Could not check who can change the hours for each day; they are left unchanged. Reload to try again."
        : isEditing && canEditDayHours === undefined
          ? "Checking who can change the hours for each day..."
          : null;

  // SA-5: warn (don't block) when the assigned lead rep is marked unavailable over the event's
  // date(s). The selected lead rep is hierarchyChain.repId (a sales_position_id); self-service / no
  // rep is skipped. Times are compared as wall-clock (availability is Central) — a soft warning, so
  // approximate cross-timezone comparison is acceptable; the manager can always override.
  const leadRepPositionId =
    hierarchyChain.repId && hierarchyChain.repId !== SELF_SERVICE_REP_VALUE
      ? hierarchyChain.repId
      : null;
  const conflictRangeEnd =
    dateRange.isDateRange && dateRange.endDate
      ? dateRange.endDate
      : dateRange.startDate;
  const { data: leadRepAvailability = [] } = useQuery({
    queryKey: [
      "eventform-rep-availability",
      leadRepPositionId,
      dateRange.startDate,
      conflictRangeEnd,
    ],
    queryFn: () =>
      getAvailabilityForPositions([leadRepPositionId as string], {
        start: dateRange.startDate,
        end: conflictRangeEnd || dateRange.startDate,
      }),
    enabled: !!leadRepPositionId && !!dateRange.startDate && !dateTbd,
  });

  const availabilityConflicts = useMemo(() => {
    if (!leadRepPositionId || !dateRange.startDate) {
      return [];
    }
    const evStart = dateRange.startDate;
    const evEnd = conflictRangeEnd || dateRange.startDate;
    return leadRepAvailability.filter((a) => {
      // MSR QA 10-20 (owner): reps take whole days off, so any block touching the event's local date(s) is a day
      // off -- older timed blocks included. No partial-day / time-overlap logic.
      return a.start_date <= evEnd && a.end_date >= evStart;
    });
  }, [leadRepAvailability, leadRepPositionId, dateRange, conflictRangeEnd]);

  // MSR QA 10-20: on save, warn when the lead OR any rep already on this event's Sales Reps list has time off on the
  // date(s) being saved (e.g. moving an event onto a rep's day off). A confirm, never a block.
  const { data: listedReps = [] } = useQuery({
    queryKey: ["event-sales-assignments", event?.id],
    queryFn: () => getEventSalesAssignments(event?.id as string),
    enabled: !!event?.id,
  });
  const savedDates = dateTbd
    ? []
    : datesInclusive(dateRange.startDate, conflictRangeEnd);
  const { data: saveTimeOff = {} } = useTimeOff(
    [
      ...(leadRepPositionId ? [leadRepPositionId] : []),
      ...listedReps.map((a) => a.sales_position_id),
    ],
    savedDates,
  );
  const [timeOffPrompt, setTimeOffPrompt] = useState<string | null>(null);
  const timeOffConfirmed = useRef(false);
  const formElRef = useRef<HTMLFormElement | null>(null);

  const leadRepLabel = useMemo(() => {
    const sp = (salesPositions ?? []).find((s) => s.id === leadRepPositionId);
    return sp ? displaySalesPositionName(sp) : "The assigned rep";
  }, [salesPositions, leadRepPositionId]);

  const [submitting, setSubmitting] = useState(false);
  const [orgLocationSelections, setOrgLocationSelections] = useState<
    EventOrgLocationSelection[]
  >([]);

  // Venue (organization) requirement — see utils/eventVenueRequirement.
  //
  // RETRO-INVALIDATION GUARD. This is the EDIT surface, so the rule must never
  // strand an event that predates it: an operator has to be able to fix the date,
  // the status or the times of an old venue-less event without first inventing an
  // organization for it. So the requirement is enforced only when the event AS
  // LOADED already satisfied it — i.e. the rule can stop a link being REMOVED, but
  // never blocks an event that arrived without one.
  //
  // Keyed on the loaded event passing the WHOLE rule (not merely "has some link"),
  // because the required field differs by type: on PROD, 31 gun shows carry an
  // `event_organizations` row but no promoter, and the promoter is the field their
  // form actually exposes — a naive "has any link" test would have blocked every
  // one of those 31 from ever being saved again.
  //
  // Starts FALSE and is set only by a successful load, so a failed/racing load
  // fails OPEN (grandfathered) rather than blocking a save.
  const loadedEventSatisfiedVenueRuleRef = useRef(false);
  // Snapshots of the associations as loaded (edit only). The edit save skips an association write whose selection
  // did not change. A non-owner manager may edit an event's details (migration 197) but not its org/location links,
  // and rewriting unchanged links would otherwise fail AFTER the event row saved (a half-saved edit).
  const loadedOrgKeyRef = useRef<string | null>(null);
  const loadedLinksKeyRef = useRef<string | null>(null);
  const loadedListsKeyRef = useRef<string | null>(null);
  const [venueError, setVenueError] = useState<{
    field: "org" | "promoter";
    message: string;
  } | null>(null);
  // Drives the required marker + helper copy. Deliberately mirrors the guard used
  // at submit, INCLUDING the grandfather clause: an old venue-less event must not
  // be decorated with a required marker it is not actually held to.
  const venueLinkIsRequired =
    venueLinkRequired({ locationType: formData.location_type, dateTbd }) &&
    !(isEditing && !loadedEventSatisfiedVenueRuleRef.current);

  // R2A (2026-05-17): when org selections change, trigger hierarchy
  // derivation against the FIRST org (deterministic + matches Heather's
  // intent for multi-org events per the operator's clarification).
  // If the hierarchy was manually overridden, surface a confirmation
  // before overwriting (audit verdict: never silently bulldoze manual
  // selections — sales relationships are matrixed and overrides are
  // first-class).
  const previousFirstOrgIdRef = useRef<string | null>(null);
  // R2A: tracks whether the form's location address fields were
  // auto-filled from an org's physical address (so subsequent org
  // changes can safely overwrite). True after an autofill; reset
  // implicitly when the operator types into any address field via
  // the handleChange wrapper below.
  const locationAutoFilledRef = useRef<boolean>(false);
  useEffect(() => {
    const firstOrgId = orgLocationSelections[0]?.org_id ?? null;
    if (firstOrgId === previousFirstOrgIdRef.current) {
      return;
    }
    // GS-06: the rep-chain fill needs the positions; wait for them (this effect re-runs
    // when salesPositions arrives) rather than falling back to the account chain.
    if (firstOrgId && salesPositions === undefined) {
      return;
    }
    previousFirstOrgIdRef.current = firstOrgId;

    if (!firstOrgId) {
      // Org cleared. Don't touch hierarchy.
      return;
    }

    (async () => {
      // Parallel — hierarchy derivation + org address autofill lookup.
      const [derived, orgAddrRow] = await Promise.all([
        deriveHierarchyForOrg(firstOrgId),
        supabase
          .from("organizations")
          .select(
            "address_line1, address_city, address_state, address_postal_code, address_country",
          )
          .eq("id", firstOrgId)
          .single()
          .then((r) => r.data),
      ]);

      // Hierarchy
      if (derived?.assignment_found) {
        // DB column is sales_director_position_id but the RPC returns
        // director_position_id (matching the real DB position_type
        // vocabulary). Bridging the impedance here.
        accountLevelsRef.current = {
          tmId: derived.territory_manager_position_id,
          rmId: derived.regional_manager_position_id,
          sdId: derived.director_position_id,
        };
        // GS-06: the managers follow the derived rep's reporting chain; the account
        // chain is only the fallback when that rep has none.
        const nextHierarchy = {
          repId: derived.sales_rep_position_id,
          ...fillFromLeadChain(
            salesPositions ?? [],
            derived.sales_rep_position_id,
            accountLevelsRef.current,
            accountLevelsRef.current,
          ).levels,
        };
        if (hierarchyManuallyOverridden.current) {
          setPendingOrgChange({ nextOrgId: firstOrgId, nextHierarchy });
        } else {
          setHierarchyChain(nextHierarchy);
        }
      }

      // Location autofill (R2A). Per audit verdict Decision 6: only
      // overwrite the form's location address fields when they are
      // EMPTY OR when they were auto-filled from a different org on a
      // prior change. locationAutoFilledRef tracks whether the current
      // values came from auto-fill (we may safely overwrite them) vs
      // operator-typed (we must not touch).
      if (orgAddrRow) {
        setFormData((prev) => {
          const hasManualAddress =
            (prev.location_address_line1 || "").trim() !== "" ||
            (prev.location_city || "").trim() !== "" ||
            (prev.location_state || "").trim() !== "" ||
            (prev.location_postal_code || "").trim() !== "";
          if (hasManualAddress && !locationAutoFilledRef.current) {
            // Operator has their own address typed — don't touch.
            return prev;
          }
          locationAutoFilledRef.current = true;
          return {
            ...prev,
            location_address_line1: orgAddrRow.address_line1 ?? "",
            location_city: orgAddrRow.address_city ?? "",
            location_state: orgAddrRow.address_state ?? "",
            location_postal_code: orgAddrRow.address_postal_code ?? "",
          };
        });
      }
    })();
  }, [orgLocationSelections, deriveHierarchyForOrg, salesPositions]);

  // #13 (2026-06): clear the org-autofilled physical address so the operator can
  // type their own. Marks the address as no-longer-autofilled so a later org
  // change won't silently re-fill over what they enter.
  const handleClearLocationAddress = () => {
    setFormData((prev) => clearLocationAddress(prev));
    locationAutoFilledRef.current = false;
  };

  const [externalLinks, setExternalLinks] = useState<EventLinkInput[]>([]);
  const [segmentSelections, setSegmentSelections] = useState<
    EventSegmentSelection[]
  >([]);
  // Event in a Box audiences linked by the launch (migration 894). Shown read-only, never part of the
  // editable selection, never deleted or re-inserted by this form's save.
  const [eibAudienceLinks, setEibAudienceLinks] = useState<ListAssociation[]>(
    [],
  );

  // Load existing organizations, locations, and links when editing
  useEffect(() => {
    if (event && event.id) {
      const loadExistingData = async () => {
        try {
          const [orgs, locs, links, listAssocs] = await Promise.all([
            getEventOrganizations(event.id),
            getEventLocations(event.id),
            getEventLinks(event.id),
            getEventListAssociations(event.id),
          ]);

          const selections: EventOrgLocationSelection[] = [];

          // Add organization-level associations
          (orgs as OrgAssociation[]).forEach((assoc) => {
            if (assoc.organizations) {
              selections.push({
                org_id: assoc.organizations.id,
                org_name: assoc.organizations.name,
                location_id: null,
              });
            }
          });

          // Add location-specific associations
          (locs as LocAssociation[]).forEach((assoc) => {
            if (assoc.locations && assoc.locations.organizations) {
              selections.push({
                org_id: assoc.locations.organization_id,
                org_name: assoc.locations.organizations.name,
                location_id: assoc.locations.id,
                location_name: assoc.locations.name,
              });
            }
          });

          // GS-06 audit P1: the loaded org is NOT an org change — seed the derive guard so
          // opening an event never re-derives (and silently replaces) its saved hierarchy.
          previousFirstOrgIdRef.current = selections[0]?.org_id ?? null;
          setOrgLocationSelections(selections);
          loadedOrgKeyRef.current = orgSelectionKey(selections);
          // Snapshot whether this event ALREADY met the venue requirement. Only
          // then is the requirement enforced on save (see the ref's comment).
          loadedEventSatisfiedVenueRuleRef.current = !eventVenueError({
            locationType: event.location_type ?? "physical",
            eventType: event.type,
            dateTbd: event.date_tbd ?? false,
            orgSelectionCount: selections.length,
            promoterId:
              (event as { promoter_id?: string | null }).promoter_id ?? null,
          });

          // Set external links
          const loadedLinks = (links as LinkAssociation[]).map((link) => ({
            id: link.id,
            url: link.url,
            label: link.label,
            description: link.description,
            link_type: link.link_type,
            display_order: link.display_order,
          }));
          setExternalLinks(loadedLinks);
          loadedLinksKeyRef.current = linksKey(loadedLinks);

          // Set segment associations (mapped from list data)
          const allAssocs = listAssocs as ListAssociation[];
          setEibAudienceLinks(allAssocs.filter(isEibAudienceLink));
          const loadedSegments = allAssocs
            .filter((assoc) => !isEibAudienceLink(assoc))
            .map((assoc) => ({
              segment_id: assoc.list_id,
              segment_name: assoc.lists?.name || "Unknown Segment",
              association_type:
                assoc.association_type as EventSegmentSelection["association_type"],
            }));
          setSegmentSelections(loadedSegments);
          loadedListsKeyRef.current = segmentsKey(loadedSegments);
        } catch (error) {
          console.error("Error loading event data:", error);
        }
      };

      loadExistingData();
    }
  }, [event]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    // R2A: any operator edit to a location address field clears the
    // "we auto-filled this" marker so subsequent org changes won't
    // overwrite their typed value.
    if (
      name === "location_address_line1" ||
      name === "location_city" ||
      name === "location_state" ||
      name === "location_postal_code"
    ) {
      locationAutoFilledRef.current = false;
    }

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      // Auto-sync location state to regulatory state
      if (name === "location_state" && value) {
        updated.state = value;
      }
      return updated;
    });
  };

  // Event Type change. Switching to a Gun Show clears any org/location selections
  // made under a prior type: the org selector is hidden for gun shows (the promoter
  // takes that slot), so leftover selections would otherwise be saved as an
  // invisible, unremovable event_organizations row and skew hierarchy derivation.
  // Clearing to [] also re-runs the hierarchy effect off an empty org (internal
  // audit MEDIUM). Legacy gun_show associations loaded on EDIT are untouched — no
  // type change fires for them.
  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleChange(e);
    if (e.target.value === "gun_show") {
      setOrgLocationSelections([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // R2 post-deploy audit defense-in-depth: even though the
    // fieldset disables every input on cancelled events, an
    // explicit submit (keyboard Enter, programmatic dispatch)
    // could still reach here. Bail before any mutation.
    if (isCancelled) {
      return;
    }

    // CK17 (2026-06): the edit form also exposes a "Completed" status option, so
    // it must enforce the same attendance-required gate as the Event Detail
    // status dropdown — otherwise an operator could complete a class/seminar/
    // speaking event without attendance straight from this form. Attendance is
    // entered on the Overview tab, so we check the existing event's count (a new
    // event has none, so creating one directly as Completed is also blocked).
    const completionBlock = eventStatusChangeBlockReason(
      formData.status,
      formData.type,
      event?.attendance_count,
      // SA-2: use the date being SAVED (TBD has no date), so correcting a wrong
      // future date in the same save unblocks completion.
      dateTbd ? null : dateRange.startDate,
    );
    if (completionBlock) {
      showToast("error", completionBlock);
      return;
    }

    if (!timeOffConfirmed.current) {
      const nameFor = (pos: string) => {
        const sp = (salesPositions ?? []).find((x) => x.id === pos);
        const listed = listedReps.find((a) => a.sales_position_id === pos);
        return sp
          ? displaySalesPositionName(sp)
          : listed?.sales_position?.assigned_name ||
              listed?.sales_position?.title ||
              "A rep";
      };
      const lines = Object.entries(saveTimeOff).map(([pos, ds]) =>
        timeOffSentence(nameFor(pos), ds),
      );
      if (lines.length) {
        setTimeOffPrompt(`${lines.join(" ")} Save anyway?`);
        return;
      }
    }
    timeOffConfirmed.current = false;

    setSubmitting(true);

    // Validate dates before submission (skip if TBD)
    if (!dateTbd) {
      // If not TBD but no date provided, show error
      if (!dateRange.startDate) {
        showToast("error", "Please select a date or mark as TBD");
        setSubmitting(false);
        return;
      }
      // Timezone is required when dates are specified
      if (!timezone) {
        showToast("error", "Please select a time zone for this event");
        setSubmitting(false);
        return;
      }
      const dateErrors = validateDateRange(dateRange);
      if (
        isEditing &&
        dateErrors.startDate &&
        dateRange.startDate === loadedStartDateRef.current
      ) {
        delete dateErrors.startDate; // unchanged past start date on an existing event
      }
      if (Object.keys(dateErrors).length > 0) {
        const errorMessage =
          dateErrors.startDate ||
          dateErrors.endDate ||
          dateErrors.dateRange ||
          "Invalid dates";
        showToast("error", errorMessage);
        setSubmitting(false);
        return;
      }
    }

    // Validate timezone when dates are specified
    if ((dateRange.startDate || dateRange.endDate) && !timezone) {
      showToast("error", "Please select a timezone for the event dates");
      setSubmitting(false);
      return;
    }

    // Hours each day: refuse to save a blank or reversed day before anything is written.
    const dayHoursPlan = dailyHoursReadOnlyReason
      ? ({ action: "none" } as const)
      : planDayHoursSave(dailyHoursApp, dailyHours, savedDayHours);
    if (dayHoursPlan.action === "invalid") {
      showToast("error", dayHoursPlan.message);
      setSubmitting(false);
      return;
    }

    // A location name is REQUIRED for walk-in-eligible events. The self check-in
    // picker (list_self_checkin_events_v2) only lists events with a non-empty
    // location_name, so without one a walk-in event silently never appears at the
    // kiosk — the exact surprise a tester hit. Block the save with a clear reason.
    if (formData.allow_walk_ins && !formData.location_name.trim()) {
      showToast(
        "error",
        "Add a location name — events that allow walk-ins need one to appear on the self check-in page.",
      );
      setSubmitting(false);
      return;
    }

    // Venue ORGANIZATION requirement (owner decision 2026-09-09). Grandfathered for
    // any event that did not already satisfy it when this form loaded, so an old
    // venue-less event stays fully editable — see
    // loadedEventSatisfiedVenueRuleRef.
    const venueErr = eventVenueError({
      locationType: formData.location_type,
      eventType: formData.type,
      dateTbd,
      orgSelectionCount: orgLocationSelections.length,
      promoterId: formData.promoter_id,
      grandfathered: isEditing && !loadedEventSatisfiedVenueRuleRef.current,
    });
    setVenueError(venueErr);
    if (venueErr) {
      showToast("error", venueErr.message);
      setSubmitting(false);
      return;
    }

    // Smart Admin replacement R1 (2026-05-17) — defensive time validation.
    // Heather reproduced 4× the bug where a submitted event saves with
    // 12:00 AM start/end despite the user entering specific PM times.
    // Speculated cause: <input type="time"> returns "" after a browser
    // autofill or All-Day toggle reset, and the submit-time fallback
    // `dateRange.startTime || "00:00"` then writes midnight without
    // telling the user. Defense: when the user opted into a timed event
    // (not TBD, not all-day), require startTime to be present. Same for
    // endTime when endDate is set. Logs a console.warn so we can
    // correlate with the original repro.
    if (!dateTbd && !dateRange.allDay && dateRange.startDate) {
      if (!dateRange.startTime) {
        console.warn(
          "[EventForm] R1 defensive guard fired: startTime missing at submit",
          { dateRange, dateTbd, timezone },
        );
        showToast(
          "error",
          "Start time is missing. Set a specific start time or toggle 'All Day Event'.",
        );
        setSubmitting(false);
        return;
      }
      if (dateRange.endDate && !dateRange.endTime) {
        console.warn(
          "[EventForm] R1 defensive guard fired: endTime missing at submit",
          { dateRange, dateTbd, timezone },
        );
        showToast(
          "error",
          "End time is missing for the end date. Set an end time or remove the end date.",
        );
        setSubmitting(false);
        return;
      }
    }

    try {
      // Combine date and time into ISO format with timezone offset
      // The offset is computed from the selected IANA timezone for the event date,
      // so DST is handled automatically (e.g., EST=-05:00 vs EDT=-04:00)
      const TBD_PLACEHOLDER_DATE = "9999-12-31";
      const tzOffset = timezone
        ? getTimezoneOffset(timezone, dateRange.startDate)
        : "";
      const start_time = dateTbd
        ? `${TBD_PLACEHOLDER_DATE}T00:00:00`
        : `${dateRange.startDate}T${dateRange.startTime || "00:00"}:00${tzOffset}`;
      // An empty end date means the same day as the start, with the entered end time (MSR activation fix 5:
      // moving the start past the end cleared the end date, and the save then sent end = start, which the
      // date writer refuses). validateDateRange already refused an end time that is not after the start time.
      const endAt = resolveEndDateTime(dateRange);
      const endTzOffset =
        timezone && endAt ? getTimezoneOffset(timezone, endAt.date) : "";
      const end_time =
        dateTbd || !endAt
          ? undefined
          : `${endAt.date}T${endAt.time}:00${endTzOffset}`;

      const eventData: Partial<Event> = {
        title: formData.title,
        description: formData.description || undefined,
        notes: formData.notes || undefined,
        type: formData.type as Event["type"],
        status: formData.status as Event["status"],
        organizer_id: formData.organizer_id || undefined,
        state: formData.state || undefined, // Regulatory state
        start_time,
        end_time,
        all_day: dateRange.allDay,
        date_tbd: dateTbd,
        timezone: dateTbd ? undefined : timezone || undefined,
        location_type: formData.location_type as Event["location_type"],
        location_name: formData.location_name || undefined,
        // Save location_address if ANY address field has a value (not just street address)
        location_address:
          formData.location_address_line1 ||
          formData.location_city ||
          formData.location_state ||
          formData.location_postal_code
            ? {
                address_line1: formData.location_address_line1 || undefined,
                city: formData.location_city || undefined,
                state: formData.location_state || undefined,
                postal_code: formData.location_postal_code || undefined,
              }
            : undefined,
        meeting_url: formData.meeting_url || undefined,
        is_public: formData.is_public,
        is_featured: formData.is_featured,
        registration_required: formData.registration_required,
        max_attendees: formData.max_attendees
          ? Number(formData.max_attendees)
          : undefined,
        // Soft pre-registration cap (null clears on edit). Only meaningful when
        // registration is required; walk-ins are never limited by it.
        pre_registration_cap:
          formData.registration_required && formData.pre_registration_cap
            ? Number(formData.pre_registration_cap)
            : null,
        allow_walk_ins: formData.allow_walk_ins,
        walk_in_limit: formData.walk_in_limit
          ? Number(formData.walk_in_limit)
          : undefined,
        is_recurring: false,
        // Only on CREATE: an edit must not silently make the editor the event's creator (that moved creator-based
        // rights on every save). Reassigning the creator is a separate, explicit change.
        created_by: isEditing ? undefined : user?.id,
        // Promoter organization ID and name.
        // Use null so clearing an existing promoter on edit persists.
        promoter_id: formData.promoter_id || null,
        promoter_name: formData.promoter_name || null,
        // Attendance list auto-creation
        create_attendance_list: formData.create_attendance_list,
        // Class sub-classifications (only for class events)
        class_delivery_method:
          formData.type === "class"
            ? formData.class_delivery_method || undefined
            : undefined,
        usls_curriculum:
          formData.type === "class"
            ? formData.usls_curriculum || undefined
            : undefined,
        class_subtype:
          formData.type === "class"
            ? formData.class_subtype || undefined
            : undefined,
        // Voucher used (for table, gun_show, conference, speaking_engagement)
        voucher_used: VOUCHER_USED_EVENT_TYPES.includes(formData.type)
          ? formData.voucher_used
          : undefined,
        // USLS Curriculum used (for class events)
        usls_curriculum_used:
          formData.type === "class" ? formData.usls_curriculum_used : undefined,
        // Event ownership & sales position
        event_ownership: formData.event_ownership || undefined,
        // R2A (2026-05-17): sales_position_id and the three TM/RM/SD slots
        // now all come from hierarchyChain (the unified state populated by
        // the new derive_account_hierarchy RPC + operator overrides).
        // The standalone "Sales Position" dropdown was removed.
        // #14: self-service events have NO lead/event rep — persist null even
        // if a rep id lingers in state, and flag is_self_service.
        sales_position_id: resolveLeadRepPositionId(
          hierarchyChain.repId,
          isSelfService,
        ),
        is_self_service: isSelfService,
        territory_manager_position_id: hierarchyChain.tmId || null,
        regional_manager_position_id: hierarchyChain.rmId || null,
        sales_director_position_id: hierarchyChain.sdId || null,
        // Phase 1.1 Ship 3: auto-mint generic CTA. Defaulted by event
        // type at create time; operators edit later if needed.
        subscription_signup_eligible: !isEditing
          ? SUBSCRIPTION_ELIGIBLE_EVENT_TYPES.includes(formData.type)
          : undefined,
      } as any;

      let savedEventId: string;
      // Edit: write an association only if it changed (null snapshot = not loaded -> treat as changed).
      const orgsChanged =
        !isEditing ||
        loadedOrgKeyRef.current === null ||
        orgSelectionKey(orgLocationSelections) !== loadedOrgKeyRef.current;
      const linksChanged =
        !isEditing ||
        loadedLinksKeyRef.current === null ||
        linksKey(externalLinks) !== loadedLinksKeyRef.current;
      const listsChanged =
        !isEditing ||
        loadedListsKeyRef.current === null ||
        segmentsKey(segmentSelections) !== loadedListsKeyRef.current;
      if (isEditing && event && orgsChanged && user?.id) {
        // Refuse BEFORE anything is written: set_event_org_associations requires event edit permission, which a
        // non-owner manager (allowed to edit details by 197) does not have.
        const { data: canEditLinks } = await supabase.rpc(
          "has_event_permission",
          {
            p_event_id: event.id,
            p_user_id: user.id,
            p_required_level: "edit",
          },
        );
        if (canEditLinks !== true) {
          throw new Error(
            "You can edit this event's details, but only an admin or the event's owner can change its organizations and locations. Undo that change to save.",
          );
        }
      }

      // MSR activation fix 6 (internal re-audit P1-1 / P2-3): on edit the Lead/Event Rep is NOT part of the event
      // update. It changes only when the operator changed the rep control, through ONE compare-and-set call that
      // moves the Sales Reps list's Lead and the field together ("no rep" / self-service takes the Lead off the
      // list). A save that did not touch the rep sends neither, so a stale form cannot revert someone else's Lead.
      let leadNotice: ReturnType<typeof leadRepSyncNotice> = null;
      if (isEditing && event) {
        // External audit 360cf0f5 P3: each baseline (rep, Self-Service) advances only after ITS write succeeded, so
        // a retry after a failed event update re-sends Self-Service instead of silently dropping it.
        const leadResult = await saveEditedLeadRepAndEvent(
          loadedRepRef,
          { repId: hierarchyChain.repId, isSelfService },
          { ...eventData } as Record<string, unknown>,
          (next, expected) => syncLeadRepToSalesReps(event.id, next, expected),
          (updates) =>
            updateMutation.mutateAsync({
              id: event.id,
              updates: updates as typeof eventData,
            }),
        );
        leadNotice = leadResult ? leadRepSyncNotice(leadResult) : null;
        savedEventId = event.id;
        showToast("success", "Event updated successfully");
        if (leadNotice) {
          showToast(leadNotice.type, leadNotice.message);
        }
      } else {
        const newEvent = await createMutation.mutateAsync(
          eventData as Omit<Event, "id" | "created_at" | "updated_at">,
        );
        savedEventId = newEvent.id;
        showToast("success", "Event created successfully");
      }

      // MSR activation fix 6, create: the chosen Lead/Event Rep also goes on the new event's Sales Reps list (the
      // source of truth for who works it). Never throws; the event is already saved.
      if (!isEditing) {
        const leadId = (eventData as { sales_position_id?: string | null })
          .sales_position_id;
        if (leadId) {
          const notice = leadRepSyncNotice(
            await syncLeadRepToSalesReps(savedEventId, leadId, leadId),
            { onCreate: true },
          );
          if (notice) {
            showToast(notice.type, notice.message);
          }
        }
      }

      // Hours each day (after the event, whose dates the server checks them against). Never fatal: the event is
      // saved; the operator is told and can re-save.
      if (dayHoursPlan.action === "set" || dayHoursPlan.action === "clear") {
        try {
          await setEventDayHours(
            savedEventId,
            dayHoursPlan.action === "set" ? dayHoursPlan.days : [],
          );
        } catch (dhErr) {
          console.warn("Saving daily hours failed:", dhErr);
          showToast(
            "error",
            `Event saved, but the hours for each day were not: ${describeDayHoursError(dhErr)}`,
          );
        }
        void queryClient.invalidateQueries({ queryKey: ["event-day-hours"] });
      }

      // Save organization/location associations. The helper clears existing
      // rows first, so call it even when the selection is empty.
      if (orgsChanged) {
        await saveEventOrgLocations(savedEventId, orgLocationSelections);
      }

      // AFTER the links: saving them prepares the generic CTA from the promoter / host (database, at
      // commit). QR + flyer before leaving the form; a no-op when the CTA already has its QR. Never
      // throws; bounded.
      if (
        SUBSCRIPTION_ELIGIBLE_EVENT_TYPES.includes(formData.type) &&
        user?.id
      ) {
        await afterEventLinksSaved(savedEventId, user.id);
      }

      // Save external links
      if (linksChanged) {
        await saveEventLinks(savedEventId, externalLinks);
      }

      // Save segment associations (mapped to list format for database)
      if (listsChanged) {
        await saveEventListAssociations(
          savedEventId,
          segmentSelections.map((s) => ({
            list_id: s.segment_id,
            list_name: s.segment_name,
            association_type: s.association_type,
          })),
        );
      }

      // On create, route capability-rich events into the guided EIB setup with the
      // correct workflow: pre_register when registration is required, else show_up
      // (a conversion/walk-up event must NOT open the registration workflow). Minimal
      // events (no conversion, no registration) skip the hand-off.
      const guidedMode: "show_up" | "pre_register" | null =
        !isEditing && (isConversionEngine || formData.registration_required)
          ? formData.registration_required
            ? "pre_register"
            : "show_up"
          : null;
      onSuccess?.(savedEventId, guidedMode);
    } catch (error) {
      console.error("Error saving event:", error);
      // A database refusal arrives as a plain object with a message (not an Error): show its readable form
      // instead of the bare "Failed to save event".
      showToast(
        "error",
        describeDateModelError(error) ??
          (error instanceof Error
            ? error.message
            : typeof (error as { message?: unknown } | null)?.message ===
                "string"
              ? (error as { message: string }).message
              : "Failed to save event"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Get selected owner name for display
  const selectedOwner = users.find((u) => u.id === formData.organizer_id);

  return (
    <>
      <ConfirmDialog
        isOpen={timeOffPrompt !== null}
        onClose={() => setTimeOffPrompt(null)}
        onConfirm={() => {
          setTimeOffPrompt(null);
          timeOffConfirmed.current = true;
          formElRef.current?.requestSubmit();
        }}
        title="A rep isn't marked available"
        message={timeOffPrompt ?? ""}
        confirmText="Save anyway"
        variant="warning"
      />
      <form ref={formElRef} onSubmit={handleSubmit} className="space-y-6">
        {isCancelled && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
            <strong>Cancelled events are read-only.</strong> All fields are
            locked. Cancellation is permanent — create a new event if you need
            to reschedule.
          </div>
        )}
        <fieldset disabled={isCancelled} style={{ display: "contents" }}>
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Basic Information
            </h3>

            <Input
              label="Event Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Enter event title"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="Event description"
              />
            </div>

            {/* Event Classification (2026-07-28) — drives which Event Types are offered
              and the capability defaults. Conversion-ness derives from `type`
              (approach C); the registration toggle binds registration_required. */}
            <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
              <p className="text-sm font-semibold text-gray-900">
                How will this event be used?
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Your answers set up the right tools for this event and filter
                the Event Type list below. You can change them anytime.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(
                  [
                    {
                      key: "conversion",
                      label: "Recruit new members at this event?",
                      hint: "Yes for booth-style events (gun shows, member check-ins) where you invite nearby members and reward booth visits and “bring-a-friend” referrals that sign up new members. No for informational events.",
                      value: isConversionEngine,
                      set: (v: boolean) =>
                        handleClassificationChange(
                          v,
                          formData.registration_required,
                        ),
                    },
                    {
                      key: "registration",
                      label: "Do attendees sign up in advance?",
                      hint: "Yes if people register ahead of time on a sign-up page — this lets you send invitations and set a limit on pre-registrations (walk-ins are still allowed). No for walk-up-only events.",
                      value: formData.registration_required,
                      set: (v: boolean) =>
                        handleClassificationChange(isConversionEngine, v),
                    },
                  ] as const
                ).map((t) => (
                  <div key={t.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.label}
                    </label>
                    <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                      {(
                        [
                          ["Yes", true],
                          ["No", false],
                        ] as const
                      ).map(([lbl, val]) => (
                        <button
                          key={lbl}
                          type="button"
                          onClick={() => t.set(val)}
                          className={`px-4 py-1.5 text-sm ${
                            t.value === val
                              ? "bg-primary-600 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {lbl}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{t.hint}</p>
                  </div>
                ))}
              </div>
            </div>

            {!event &&
              (isConversionEngine || formData.registration_required) && (
                <p className="text-xs text-primary-700 bg-primary-50 border border-primary-100 rounded px-3 py-2">
                  After you create this event, we&apos;ll open the guided setup
                  to configure{" "}
                  {formData.registration_required
                    ? "registration, invitations, and rewards"
                    : "member invitations and booth rewards"}
                  .
                </p>
              )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleTypeChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select event type...</option>
                  {classifiedTypeOptions.map((et) => (
                    <option key={et.value} value={et.value}>
                      {et.label}
                    </option>
                  ))}
                </select>
                {classifiedTypeOptions.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No event types match this combination yet — adjust the
                    classification above.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={isEditing && event?.status === "cancelled"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="in_progress">In Progress</option>
                  {/* Completed: admin/superadmin only (2026-09-30). A non-admin editing an already-completed
                    event still sees it (disabled) so the value displays and is saved unchanged. */}
                  {completedOptionState(user?.role, event?.status) !==
                    "hidden" && (
                    <option
                      value="completed"
                      disabled={
                        completedOptionState(user?.role, event?.status) ===
                        "shown_disabled"
                      }
                    >
                      Completed
                    </option>
                  )}
                  <option value="cancelled">Cancelled</option>
                  <option value="postponed">Postponed</option>
                </select>
                {isEditing && event?.status === "cancelled" && (
                  <p className="mt-1 text-xs text-gray-500">
                    Cancelled events are locked and cannot be reactivated.
                  </p>
                )}
              </div>
            </div>

            {/* Class Sub-Classifications (shown when type = class) */}
            {formData.type === "class" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <h4 className="text-sm font-semibold text-blue-900">
                  Class Details
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Delivery Method
                    </label>
                    <select
                      name="class_delivery_method"
                      value={formData.class_delivery_method}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select...</option>
                      {CLASS_DELIVERY_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    {/* Smart Admin replacement R1 (2026-05-17): label renamed
                    from "USLS Curriculum" to "Curriculum" per Heather
                    page 2 — not all curriculums are USLS; the
                    `usls_curriculum_used` checkbox below toggles that
                    specifically. Column name (usls_curriculum) and
                    options constant stay as-is to avoid migration. */}
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Curriculum
                    </label>
                    <select
                      name="usls_curriculum"
                      value={formData.usls_curriculum}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select...</option>
                      {USLS_CURRICULUM_OPTIONS.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Class Subtype
                    </label>
                    <select
                      name="class_subtype"
                      value={formData.class_subtype}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select...</option>
                      {CLASS_SUBTYPES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <input
                    type="checkbox"
                    id="usls_curriculum_used"
                    name="usls_curriculum_used"
                    checked={formData.usls_curriculum_used as boolean}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        usls_curriculum_used: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label
                    htmlFor="usls_curriculum_used"
                    className="text-sm font-medium text-gray-700"
                  >
                    USLS Curriculum Used
                  </label>
                </div>
              </div>
            )}

            {/* Voucher Used toggle (for table, gun_show, conference, speaking_engagement) */}
            {VOUCHER_USED_EVENT_TYPES.includes(formData.type) && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="voucher_used"
                  name="voucher_used"
                  checked={formData.voucher_used as boolean}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      voucher_used: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label
                  htmlFor="voucher_used"
                  className="text-sm font-medium text-gray-700"
                >
                  Voucher Used at This Event
                </label>
              </div>
            )}

            {/* Event Ownership & Sales Rep Assignment */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Ownership
                </label>
                <select
                  name="event_ownership"
                  value={formData.event_ownership}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select...</option>
                  {EVENT_OWNERSHIP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              {/* The old "Sales Position" dropdown was removed in R2A \u2014 its
              role is subsumed by the new Sales Rep field in the Sales
              Hierarchy section below. events.sales_position_id is still
              populated (from hierarchyChain.repId on save) so historical
              data and downstream consumers stay intact. */}
            </div>

            {/* Event Owner - Required autocomplete field */}
            <div className="relative" style={{ zIndex: 30 }}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Owner <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={
                    selectedOwner
                      ? selectedOwner.full_name || selectedOwner.email
                      : ownerSearchTerm
                  }
                  onChange={(e) => {
                    setOwnerSearchTerm(e.target.value);
                    setFormData((prev) => ({ ...prev, organizer_id: "" }));
                    setShowOwnerDropdown(true);
                  }}
                  onFocus={() => setShowOwnerDropdown(true)}
                  onBlur={() =>
                    setTimeout(() => setShowOwnerDropdown(false), 200)
                  }
                  onKeyDown={preventEnterSubmit}
                  placeholder="Search for a user..."
                  required={!formData.organizer_id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
                {formData.organizer_id && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, organizer_id: "" }));
                      setOwnerSearchTerm("");
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              {showOwnerDropdown &&
                filteredUsers.length > 0 &&
                !formData.organizer_id && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {filteredUsers.slice(0, 25).map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            organizer_id: u.id,
                          }));
                          setOwnerSearchTerm("");
                          setShowOwnerDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                      >
                        <div className="font-medium text-gray-900">
                          {u.full_name || "No name"}
                        </div>
                        <div className="text-sm text-gray-500">{u.email}</div>
                      </button>
                    ))}
                  </div>
                )}
              {showOwnerDropdown &&
                filteredUsers.length === 0 &&
                ownerSearchTerm && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg p-4 text-gray-500 text-sm">
                    No users found matching "{ownerSearchTerm}"
                  </div>
                )}
            </div>

            {/* Regulatory State - Required for CTAs and insurance purposes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Regulatory State <span className="text-red-500">*</span>
              </label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select state...</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} - {s.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Required for insurance/regulatory purposes and Call to Action
                URLs
              </p>
            </div>
          </div>

          {/* Date & Time with Date Range Picker */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Date & Time</h3>

            {/* Date TBD Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="dateTbd"
                checked={dateTbd}
                onChange={(e) => {
                  setDateTbd(e.target.checked);
                  // Clear date fields when marking as TBD
                  if (e.target.checked) {
                    setDateRange({
                      startDate: "",
                      startTime: "",
                      endDate: "",
                      endTime: "",
                      allDay: false,
                    });
                  }
                }}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
              />
              <label
                htmlFor="dateTbd"
                className="ml-2 block text-sm text-gray-900 cursor-pointer"
              >
                Date to be determined (TBD)
              </label>
            </div>

            {dateTbd ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  This event's date will be shown as "TBD" until a specific date
                  is set.
                </p>
              </div>
            ) : (
              <>
                <DateRangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  allowPastDates={isEditing}
                />
                <TimezoneSelector
                  value={timezone}
                  hint={
                    timezoneManuallySet.current
                      ? ""
                      : describeTimezoneSource(tzAuto.resolved)
                  }
                  note={
                    timezoneManuallySet.current ? "" : timezoneUnsetNote(tzAuto)
                  }
                  onChange={(tz) => {
                    if (!tz) {
                      // Back to "Select timezone...": hand the zone back to the ZIP rule.
                      timezoneManuallySet.current = false;
                      setTzRederive((n) => n + 1);
                      return;
                    }
                    setTimezone(tz);
                    timezoneManuallySet.current = true;
                    setTzRederive((n) => n + 1);
                  }}
                  required={!!dateRange.startDate}
                />
                <EventDailyHoursEditor
                  app={dailyHoursApp}
                  value={dailyHours}
                  onChange={setDailyHours}
                  saved={savedDayHours}
                  readOnlyReason={dailyHoursReadOnlyReason}
                  loadError={dailyHoursLoadError}
                />
              </>
            )}
            {dateTbd &&
              (savedDayHours?.length ?? 0) > 0 &&
              !dailyHoursReadOnlyReason && (
                <p
                  className="text-sm text-gray-600"
                  data-testid="daily-hours-kept"
                >
                  The date is now TBD. The hours already saved for each day are
                  kept, but not shown while the date is TBD.
                </p>
              )}
          </div>

          {/* Organization slot. Gun Shows swap the PROMOTER into this spot (the promoter
            runs the show and its address is NOT the venue — 2026-07-28 redesign); the
            separate Promoter section below is hidden for them. Every other type keeps the
            normal org/location selector. 'show' (deprecated) hides it entirely. */}
          {formData.type === "gun_show" ? (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Promoter Organization{" "}
                {venueLinkIsRequired && <span className="text-red-500">*</span>}
              </h3>
              <p className="text-sm text-gray-500">
                Gun shows are run by a promoter. The promoter&apos;s address is
                not the show location — enter the show address under Location
                below.
              </p>
              <PromoterSelector
                value={formData.promoter_id || null}
                onChange={(promoterId, promoterName) => {
                  setVenueError(null);
                  setFormData((prev) => ({
                    ...prev,
                    promoter_id: promoterId || "",
                    promoter_name: promoterName || "",
                  }));
                }}
              />
              {venueError?.field === "promoter" && (
                <p className="mt-1 text-sm text-red-600">
                  {venueError.message}
                </p>
              )}
            </div>
          ) : formData.type !== "show" ? (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Associated Organizations & Locations{" "}
                {venueLinkIsRequired && <span className="text-red-500">*</span>}
              </h3>
              <p className="text-sm text-gray-500">
                {venueLinkIsRequired
                  ? "In-person events must be associated with the organization (or specific location) hosting them."
                  : "Associate this event with organizations or specific locations"}
              </p>

              <EventOrganizationLocationSelector
                selected={orgLocationSelections}
                onChange={(sel) => {
                  setVenueError(null);
                  setOrgLocationSelections(sel);
                }}
              />
              {venueError?.field === "org" && (
                <p className="mt-1 text-sm text-red-600">
                  {venueError.message}
                </p>
              )}
            </div>
          ) : null}

          {/* Location */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Location</h3>
              {(formData.location_type === "physical" ||
                formData.location_type === "hybrid") &&
                hasLocationAddress(formData) &&
                !isCancelled && (
                  <button
                    type="button"
                    onClick={handleClearLocationAddress}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700"
                    title="Clear the address so you can enter your own (e.g. after it auto-filled from the organization)."
                  >
                    Clear address
                  </button>
                )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Type
              </label>
              <select
                name="location_type"
                value={formData.location_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="physical">Physical</option>
                <option value="virtual">Virtual</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>

            {(formData.location_type === "physical" ||
              formData.location_type === "hybrid") && (
              <>
                <Input
                  label="Location Name"
                  name="location_name"
                  value={formData.location_name}
                  onChange={handleChange}
                  placeholder="e.g., Conference Center"
                  helperText="This is how the venue is named in invitation emails (e.g. “Range Night at …”). Use its everyday name, without Inc/LLC or “Range Night”."
                />
                {formData.allow_walk_ins && !formData.location_name.trim() && (
                  <p className="mt-1 text-xs text-amber-600">
                    Required for walk-in events — this is the venue name shown
                    on the self check-in page, and the event won’t appear there
                    without it.
                  </p>
                )}

                <Input
                  label="Address"
                  name="location_address_line1"
                  value={formData.location_address_line1}
                  onChange={handleChange}
                  placeholder="Street address"
                />

                <div className="grid grid-cols-3 gap-4">
                  <Input
                    label="City"
                    name="location_city"
                    value={formData.location_city}
                    onChange={handleChange}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="location_state"
                      value={formData.location_state}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select state...</option>
                      {US_STATES.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.code} - {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Postal Code"
                    name="location_postal_code"
                    value={formData.location_postal_code}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}

            {(formData.location_type === "virtual" ||
              formData.location_type === "hybrid") && (
              <Input
                label="Meeting URL"
                name="meeting_url"
                value={formData.meeting_url}
                onChange={handleChange}
                placeholder="https://zoom.us/..."
              />
            )}

            {/* State field for virtual events (required for insurance regulations) */}
            {formData.location_type === "virtual" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  name="location_state"
                  value={formData.location_state}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select state...</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.code} - {s.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Required for insurance/legal purposes
                </p>
              </div>
            )}
          </div>

          {/* R2A Sales Hierarchy section. Four editable open-data
            dropdowns. Auto-populated from the account's primary
            territorial assignment when the operator picks an org;
            each field is independently overridable for matrixed cases
            (e.g. an event managed by a different RM than the account's
            owner). Visible for ALL event types per Heather page 4. */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Sales Hierarchy
              </p>
              {hierarchyManuallyOverridden.current && (
                <span className="text-xs text-yellow-700 bg-yellow-100 border border-yellow-200 rounded-full px-2 py-0.5">
                  manually edited
                </span>
              )}
            </div>
            {/* SA-5: soft scheduling-conflict warning (override allowed). */}
            {availabilityConflicts.length > 0 && (
              <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
                <p className="font-semibold">
                  ⚠️ {leadRepLabel} is marked unavailable during this event
                </p>
                <ul className="mt-1 list-disc list-inside space-y-0.5">
                  {availabilityConflicts.map((a) => (
                    <li key={a.id}>
                      {AVAILABILITY_TYPE_LABELS[a.availability_type]}
                      {a.title ? ` (${a.title})` : ""} —{" "}
                      {a.start_date === a.end_date
                        ? a.start_date
                        : `${a.start_date} – ${a.end_date}`}
                      {a.is_all_day === false && a.start_time && a.end_time
                        ? ` ${a.start_time.slice(0, 5)}–${a.end_time.slice(0, 5)} CT`
                        : ""}
                    </li>
                  ))}
                </ul>
                <p className="mt-1">
                  You can still save this assignment (override).
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                {
                  // R2B (2026-05-17): per audit Decision 6, this column on
                  // events represents the "lead/event rep" — the main rep
                  // on the event. Credited-rep (per-voucher attribution)
                  // lives on CTAs, not here. Three meanings of
                  // sales_position_id are now in distinct columns:
                  //   - account-owning rep: derived via derive_account_hierarchy RPC
                  //   - lead/event rep: events.sales_position_id (this field)
                  //   - credited rep (per CTA): event_call_to_actions.sales_position_id
                  label: "Lead/Event Rep",
                  slot: "repId" as const,
                  allowed: LEAD_EVENT_REP_POSITION_TYPES,
                },
                {
                  label: "Territory Manager",
                  slot: "tmId" as const,
                  allowed: TERRITORY_MANAGER_POSITION_TYPES,
                },
                {
                  label: "Regional Manager",
                  slot: "rmId" as const,
                  allowed: REGIONAL_MANAGER_POSITION_TYPES,
                },
                {
                  label: "Sales Director",
                  slot: "sdId" as const,
                  allowed: SALES_DIRECTOR_POSITION_TYPES,
                },
              ].map(({ label, slot, allowed }) => {
                const choices = sortSalesPositionsByFirstName(
                  (salesPositions ?? []).filter((sp) =>
                    allowed.includes(sp.position_type),
                  ),
                );
                const isRepSlot = slot === "repId";
                const currentValue = isRepSlot
                  ? repSelectValue(hierarchyChain.repId, isSelfService)
                  : (hierarchyChain[slot] ?? "");
                return (
                  <div key={slot}>
                    <label className="block text-xs text-gray-600 mb-1">
                      {label}
                    </label>
                    <select
                      value={currentValue}
                      onChange={(e) => {
                        hierarchyManuallyOverridden.current = true;
                        if (isRepSlot) {
                          const parsed = parseRepSelectChange(e.target.value);
                          setIsSelfService(parsed.isSelfService);
                          // GS-06: picking the Lead/Event Rep refills the three manager
                          // levels from that rep's reporting chain (still editable).
                          setHierarchyChain((prev) => ({
                            ...prev,
                            ...(parsed.repId
                              ? fillFromLeadChain(
                                  salesPositions ?? [],
                                  parsed.repId,
                                  prev,
                                  // audit P3: once the operator edited the managers, a rep
                                  // without a chain keeps their values (no account overwrite).
                                  managersHandEditedRef.current
                                    ? null
                                    : accountLevelsRef.current,
                                ).levels
                              : {}),
                            repId: parsed.repId,
                          }));
                        } else {
                          managersHandEditedRef.current = true;
                          setHierarchyChain((prev) => ({
                            ...prev,
                            [slot]: e.target.value || null,
                          }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500 bg-white"
                    >
                      <option value="">Select…</option>
                      {/* If the currently saved value isn't in the
                        filtered allowed list (e.g. someone overrode with
                        a wider role), surface it so it doesn't disappear */}
                      {isRepSlot && (
                        <option value={SELF_SERVICE_REP_VALUE}>
                          {SELF_SERVICE_REP_LABEL}
                        </option>
                      )}
                      {currentValue &&
                        currentValue !== SELF_SERVICE_REP_VALUE &&
                        !choices.find((c) => c.id === currentValue) &&
                        (() => {
                          const sp = (salesPositions ?? []).find(
                            (s) => s.id === currentValue,
                          );
                          return (
                            <option value={currentValue}>
                              {sp
                                ? displaySalesPositionName(sp)
                                : "(unknown position)"}
                            </option>
                          );
                        })()}
                      {choices.map((sp) => (
                        <option key={sp.id} value={sp.id}>
                          {displaySalesPositionName(sp)}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
            <SalesHierarchyChainNote
              salesPositions={salesPositions ?? []}
              leadId={isSelfService ? null : leadRepPositionId}
              current={hierarchyChain}
              eventId={event?.id ?? null}
              onUseLeadChain={(levels) => {
                hierarchyManuallyOverridden.current = true;
                setHierarchyChain((prev) => ({ ...prev, ...levels }));
              }}
            />
            <p className="mt-3 text-xs text-gray-500">
              The Lead/Event Rep comes from the partner account when you pick an
              organization above; the Territory Manager, Regional Manager and
              Sales Director follow that rep&apos;s reporting chain. Override
              any field for matrixed events.
            </p>
          </div>

          {/* Attendee Segments -- none for a class / table / seminar / speaking engagement (owner 2026-10-29:
              no audience lists; the server refuses the link, migration 1260). */}
          {!eventTypeIsNoPromo(formData.type) && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Attendee Segments
              </h3>
              <p className="text-sm text-gray-500">
                Associate segments with this event for invitation, registration,
                or attendance tracking
              </p>

              {eibAudienceLinks.length > 0 && (
                <div
                  className="rounded-md border border-indigo-200 bg-indigo-50 p-3"
                  data-testid="eib-audience-links-readonly"
                >
                  <p className="text-sm font-medium text-indigo-900">
                    Linked by Event in a Box
                  </p>
                  <ul className="mt-1 space-y-1">
                    {eibAudienceLinks.map((assoc) => (
                      <li key={assoc.list_id} className="text-sm text-gray-800">
                        {eibAudienceLabel(assoc)}
                        <span className="text-gray-500">
                          {" "}
                          · {assoc.lists?.name ?? "Snapshot list"}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-xs text-indigo-800">
                    This is the frozen invitation audience of the launch. It
                    stays linked to the event and is not changed by saving this
                    form.
                  </p>
                </div>
              )}

              <EventSegmentSelector
                selected={segmentSelections}
                onChange={setSegmentSelections}
                excludeIds={eibAudienceLinks.map((assoc) => assoc.list_id)}
              />
            </div>
          )}

          {/* Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Settings</h3>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="is_public"
                  checked={formData.is_public}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label className="text-sm font-medium text-gray-700">
                  Public Event
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label className="text-sm font-medium text-gray-700">
                  Featured Event
                </label>
              </div>

              {/* "Registration Required" moved to the Event Classification toggle
                at the top of the form (2026-07-28 redesign) — single source. */}

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="allow_walk_ins"
                  checked={formData.allow_walk_ins}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label className="text-sm font-medium text-gray-700">
                  Allow Walk-ins
                  <span className="text-xs text-gray-500 ml-2">
                    (for conventions/shows)
                  </span>
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="create_attendance_list"
                  checked={formData.create_attendance_list}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label className="text-sm font-medium text-gray-700">
                  Create Attendance Segment
                  <span className="text-xs text-gray-500 ml-2">
                    (auto-creates a segment to track invites and attendance)
                  </span>
                </label>
              </div>
            </div>

            {formData.registration_required && (
              <Input
                label="Max Attendees (optional)"
                type="number"
                name="max_attendees"
                value={formData.max_attendees}
                onChange={handleChange}
                placeholder="Leave blank for unlimited"
              />
            )}

            {formData.registration_required && (
              <div>
                <Input
                  label="Max pre-registrations — members + guests (soft cap)"
                  type="number"
                  name="pre_registration_cap"
                  value={formData.pre_registration_cap}
                  onChange={handleChange}
                  placeholder="Leave blank for unlimited pre-registrations"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Limits pre-registrations only — walk-ins at the event are
                  never blocked by this cap.
                </p>
              </div>
            )}

            {formData.allow_walk_ins && (
              <Input
                label="Walk-in Limit (optional)"
                type="number"
                name="walk_in_limit"
                value={formData.walk_in_limit}
                onChange={handleChange}
                placeholder="Leave blank for unlimited walk-ins"
              />
            )}
          </div>

          {/* Promoter Information — hidden for Gun Shows (their promoter is set in the
            Organization slot above). Optional sponsor/partner for other types. */}
          {formData.type !== "gun_show" && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Promoter Organization
              </h3>
              <p className="text-sm text-gray-500">
                Select the promoter, sponsor, or partner organization for this
                event
              </p>

              <PromoterSelector
                value={formData.promoter_id || null}
                onChange={(promoterId, promoterName) => {
                  setFormData((prev) => ({
                    ...prev,
                    promoter_id: promoterId || "",
                    promoter_name: promoterName || "",
                  }));
                }}
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Internal Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Internal notes (not visible to attendees)"
            />
          </div>

          {/* External Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              External Links
            </h3>
            <p className="text-sm text-gray-500">
              Add links for event signup forms, marketing pages, social media,
              and other external resources
            </p>
            <EventExternalLinksManager
              links={externalLinks}
              onChange={setExternalLinks}
            />
          </div>

          {/* R2 post-deploy audit: close the disabled fieldset
            BEFORE the action row so Cancel/back stays clickable
            on a cancelled event. Save is guarded explicitly. */}
        </fieldset>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || isCancelled}
          >
            {submitting ? "Saving..." : isEditing ? "Save" : "Create Event"}
          </Button>
        </div>

        {/* R2A confirmation modal — fires when the operator changes the
          organization on an event whose sales hierarchy was manually
          overridden. Audit verdict: never silently overwrite manual
          values; confirm first. */}
        <Modal
          isOpen={pendingOrgChange !== null}
          onClose={() => setPendingOrgChange(null)}
          title="Replace sales hierarchy?"
        >
          {pendingOrgChange && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                You manually adjusted one or more sales hierarchy fields on this
                event. Changing the organization will replace those selections
                with the new organization's rep and that rep&apos;s reporting
                chain.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPendingOrgChange(null)}
                >
                  Keep my selections
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setHierarchyChain(pendingOrgChange.nextHierarchy);
                    hierarchyManuallyOverridden.current = false;
                    setPendingOrgChange(null);
                  }}
                >
                  Replace selections
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </form>
    </>
  );
};

export default EventForm;
